import Layer from './Layer';
import Slot from './Slot';
import CharacterDragged from './CharacterDragged';
import CharacterSimple from './CharacterSimple';
import GotoMarker from '../ui/GotoMarker';
import ClothDragged from '../ui/dressup/ClothDragged';
import { shuffle } from '../../utils/utils';
import { MODE_SEEK } from '../constants/AppConstants';

const GROUND_Y = 640;

export default class Location extends Phaser.Group {
  constructor(game, locationData, sceneWidth) {
    super(game);
    this.locationData = locationData;
    this.sceneWidth = sceneWidth;

    //
    this.isDraggingScene = false;
    this.pointer = null;
    this.startPointerX = 0;
    this.startSceneX = 0;
    this.controlsEnabled = false;

    // events
    this.onFindChar = new Phaser.Signal();

    //
    this.sky = new Layer(this.game, locationData.sky, true);
    this.background = new Layer(this.game, locationData.background, true);
    this.midground = this.game.make.group(); // layer with seeker
    this.foreground = new Layer(this.game, locationData.foreground);
    this.midUI = this.game.make.group(); // used to place dress up buttons
    this.forUI = this.game.make.group();

    //
    this.placeSlots();

    //
    this.addChild(this.sky);
    this.addChild(this.background);
    this.addChild(this.midground);
    this.addChild(this.foreground);
    this.addChild(this.midUI);
    this.addChild(this.forUI);
  }

  get width() {
    return this.locationData.width;
  }

  enableControls() {
    this.controlsEnabled = true;
    if (this.game.controller.gameMode === MODE_SEEK) {
      this.background.onChildInputDown.add(this.handleSeekInputDown, this);
    } else {
      this.background.onChildInputDown.add(this.handleHideInputDown, this);
      this.background.onChildInputUp.add(this.handleHideInputUp, this);
      this.pointer = null;
    }
  }

  disableControls() {
    this.controlsEnabled = false;
    if (this.game.controller.gameMode === MODE_SEEK) {
      this.background.onChildInputDown.remove(this.handleSeekInputDown, this);
    } else {
      this.background.onChildInputDown.remove(this.handleHideInputDown, this);
      this.background.onChildInputUp.remove(this.handleHideInputUp, this);
      this.pointer = null;
    }
  }

  placeSlots() {
    this.slots = {};
    for (let i = 0; i < this.locationData.slots.length; i++) {
      const slotData = this.locationData.slots[i];
      const slot = new Slot(this.game, slotData);
      if (slotData.layer === 'bg') {
        this.background.addSlot(slot);
        // slot.obj.tint = 0xff00ff;
        // console.log(slot);
      } else {
        this.foreground.addSlot(slot);
        // slot.object.tint = 0x00ffff;
      }

      // store slot in object
      this.slots[slotData.id] = slot;
    }
  }

  // ---------------------------------------------------------------------------
  // SEEK mode
  // ---------------------------------------------------------------------------
  placeCharacters(data, availableSlotIds, difficulty) {
    data = shuffle(data);
    for (let i = 0; i < data.length; i++) {
      const charData = data[i];
      let slot;
      if (!charData.slot) {
        // place randomly
        let diff = difficulty[i]; // get difficulty in order - first should be with higher difficulty
        while (!slot && diff >= 0) {
          slot = this.getFreeSlot(charData.id, availableSlotIds, diff);
          diff--;
        }
      } else {
        slot = this.slots[charData.slot];
      }
      slot.addCharacter(charData.id);

      // add event handler
      slot.onFindChar.add(this.handleFindChar, this);
    }
  }

  addOnFindToSlots() {
    for (const key in this.slots) {
      if (this.slots.hasOwnProperty(key)) {
        const slot = this.slots[key];
        slot.onFindChar.add(this.handleFindChar, this);
      }
    }
  }

  handleFindChar(characterId) {
    this.onFindChar.dispatch(characterId);
  }

  getFreeSlot(characterId, availableSlotIds, difficulty) {
    let slot;
    let slotData;
    // get free slots and with proper difficulty for character
    const slotsToDraw = [];
    for (let i = 0; i < availableSlotIds.length; i++) {
      slotData = this.locationData.slots.find((e) => e.id === availableSlotIds[i]);
      slot = this.slots[slotData.id];
      const charData = slotData.characters.find((e) => e.id === characterId);
      if (slot.isEmpty && charData && charData.score === difficulty) {
        slotsToDraw.push(slot);
      }
    }

    slot = slotsToDraw[Math.floor(Math.random() * slotsToDraw.length)];

    // draw slot from filtered
    // do {
    //   const ind = (this.locationData.slots.length * Math.random()) | 0;
    //   slotData = this.locationData.slots[ind];
    //   slot = this.slots[slotData.id];
    // } while (!slot.isEmpty || !slotData.characters.find((e) => e.id === characterId));
    return slot;
  }

  handleSeekInputDown(sprite, pointer) {
    // y: 570-720
    // if (pointer.y > 500) {
    const destX = -this.x + pointer.x;
    this.seeker.gotoPosition(destX);
    // }
  }

  // ---------------------------------------------------------------------------
  // HIDE mode
  // ---------------------------------------------------------------------------
  placeDragCharacter(characterId, slotRef, clothSlotRef, clothId, wx, wy, pointer) {
    const lx = -this.x + wx;
    const ly = -this.y + wy;

    this.draggable = new CharacterDragged(
      this.game,
      lx,
      ly,
      characterId,
      slotRef,
      clothSlotRef,
      clothId
    );
    this.addChild(this.draggable);
    this.draggable.startDrag(pointer);

    //
    const x = this.game.width * 0.5 - this.x;
    this.camera.setPosition(x);
    this.isDraggingCharacter = true;
  }

  removeDragCharacter() {
    this.removeChild(this.draggable);
    this.draggable = null;
    this.isDraggingCharacter = false;
  }

  tryAddCharToSlot(character) {
    const x = character.x;
    const y = character.y;
    let targetSlot;
    let minDist = 999;

    for (const key in this.slots) {
      if (this.slots.hasOwnProperty(key)) {
        const slot = this.slots[key];
        // if (slot.isEmpty && slot.enabled) {
        if (slot.enabled) {
          const center = slot.getCenter(character.characterId);
          if (center) {
            const dx = x - center.x;
            const dy = y - center.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 150 && dist < minDist) {
              targetSlot = slot;
              minDist = dist;
            }
          }
        }
      }
    }

    if (targetSlot) {
      // check if slot is empty
      const wasSlotEmpty = targetSlot.isEmpty;
      if (!wasSlotEmpty) {
        // return character form slot
        targetSlot.returnCharacter();
      }

      // add new character
      targetSlot.addCharacter(character.characterId, character.slotRef);
      targetSlot.hudSlotRef = character.slotRef;

      // clothes
      if (character.clothId > -1) {
        const clothData = this.game.controller.gameData.clothes.find(
          (d) => d.id === character.clothId
        );
        targetSlot.character.wearCloth(character.clothSlotRef, clothData);
      }

      //
      return { added: true, slot: targetSlot, wasSlotEmpty: wasSlotEmpty };
    }

    return { added: false };
  }

  disableAllSlots() {
    for (const key in this.slots) {
      if (this.slots.hasOwnProperty(key)) {
        const slot = this.slots[key];
        slot.enabled = false;
      }
    }
  }

  enableSlot(slotId) {
    this.slots[slotId].enabled = true;
  }

  disableSlot(slotId) {
    this.slots[slotId].enabled = false;
  }

  showSlotMarker(slotId) {
    this.slots[slotId].showMarker();
  }

  hideSlotMarker(slotId) {
    this.slots[slotId].hideMarker();
  }

  showSlotMarkers(characterId) {
    characterId;
    for (const key in this.slots) {
      if (this.slots.hasOwnProperty(key)) {
        const slot = this.slots[key];
        // const center = slot.getCenter(characterId);
        // if (center) {
        // center.x += this.x;
        // if (center.x > 50 && center.x < this.game.width - 50) {
        slot.showMarker();
        // }
        // }
      }
    }
  }

  hideSlotMarkers() {
    for (const key in this.slots) {
      if (this.slots.hasOwnProperty(key)) {
        const slot = this.slots[key];
        slot.hideMarker();
      }
    }
  }

  lockCharacters() {
    for (const key in this.slots) {
      if (this.slots.hasOwnProperty(key)) {
        const slot = this.slots[key];
        slot.lockCharacter();
      }
    }
  }

  unlockCharacters() {
    for (const key in this.slots) {
      if (this.slots.hasOwnProperty(key)) {
        const slot = this.slots[key];
        slot.unlockCharacter();
      }
    }
  }

  hideCharacterDressUpButtons(exceptionId = null) {
    for (const key in this.slots) {
      if (this.slots.hasOwnProperty(key)) {
        const slot = this.slots[key];
        slot.hideDressUpButtons(exceptionId);
      }
    }
  }

  showCharacterDressUpButtons() {
    for (const key in this.slots) {
      if (this.slots.hasOwnProperty(key)) {
        const slot = this.slots[key];
        slot.showDressUpButtons();
      }
    }
  }

  getSlotsWithChars() {
    const slots = [];
    for (const key in this.slots) {
      if (this.slots.hasOwnProperty(key)) {
        const slot = this.slots[key];
        if (!slot.isEmpty) {
          slots.push(slot);
        }
      }
    }
    return slots;
  }

  handleHideInputDown(sprite, pointer) {
    sprite;
    this.isDraggingScene = true;
    this.pointer = pointer;
    this.startPointerX = pointer.x;
    this.startSceneX = this.game.width * 0.5 - this.x;
    this.camera.setPosition(this.startSceneX);
  }

  handleHideInputUp(sprite, pointer) {
    sprite;
    pointer;
    this.isDraggingScene = false;
    this.pointer = null;
  }

  placeDragCloth(clothSlotRef, clothData, pointer) {
    // const wp = clothSlotRef.getWP();
    const wx = pointer.x; // wp.x;
    const wy = pointer.y; // wp.y;
    const lx = -this.x + wx;
    const ly = -this.y + wy;

    this.draggable = new ClothDragged(this.game, lx, ly, clothData, clothSlotRef);
    this.addChild(this.draggable);
    this.draggable.startDrag(pointer);

    //
    // const x = this.game.width * 0.5 - this.x;
    // this.camera.setPosition(x);
  }

  removeDragCloth() {
    this.removeChild(this.draggable);
    this.draggable = null;
  }

  // ---------------------------------------------------------------------------
  // COMMON
  // ---------------------------------------------------------------------------
  findSlotWithCharacter(characterId) {
    for (const key in this.slots) {
      if (this.slots.hasOwnProperty(key)) {
        const slot = this.slots[key];
        if (!slot.isEmpty && slot.characterId === characterId) {
          return slot;
        }
      }
    }
    return null;
  }

  getSlotById(id) {
    return this.slots[id];
  }

  addGotoMarker(x) {
    this.marker = new GotoMarker(this.game, x, GROUND_Y);
    this.midground.addChildAt(this.marker, 0);
  }

  removeGotoMarker() {
    if (this.marker) {
      this.marker.destroy();
      this.marker = null;
    }
  }

  step(dt) {
    dt;

    if (this.isDraggingScene && this.pointer) {
      const dx = this.pointer.x - this.startPointerX;
      this.camera.destX = this.startSceneX - dx;
      // console.log(this.camera.destX);
    }

    if (this.isDraggingCharacter && this.controlsEnabled) {
      const left = 150;
      const right = this.game.width - left;
      const x = this.draggable.worldPosition.x;
      const speed = 5;
      if (x < left) {
        // scroll left
        if (this.x < 0) {
          // can scroll left
          // this.draggable.dx -= speed;
          this.camera.destX -= speed;
        }
      } else if (x > right) {
        // scroll right
        if (this.x > -this.sceneWidth + this.game.width) {
          // can scroll right
          // this.draggable.dx += speed;
          this.camera.destX += speed;
        }
      } else {
        const x = this.game.width * 0.5 - this.x;
        this.camera.destX = x;
      }
    }

    // paralax effect
    const offset = this.x / (this.width - this.game.width);
    this.sky.x = offset * (this.sky.width - this.width);
    this.foreground.x = offset * (this.foreground.width - this.width);

    this.forUI.x = this.foreground.x;
  }

  getForegrounOffsetForX(x) {
    const offset = x / (this.width - this.game.width);
    return offset * (this.foreground.width - this.width);
  }

  //
  placeSeeker(seeker) {
    this.seeker = seeker;
    this.midground.addChild(this.seeker);
    this.seeker.y = GROUND_Y;

    //
    if (this.seeker.follower) {
      this.midground.addChildAt(this.seeker.follower, 0);
      this.seeker.follower.y = GROUND_Y;
    }
  }

  //
  addSimpleCharacter(charId, x, dir) {
    if (!this.simpleChars) {
      this.simpleChars = {};
    }

    const char = new CharacterSimple(this.game, x, GROUND_Y, charId, dir);
    this.midground.addChildAt(char, 0);
    this.simpleChars[charId] = char;
  }

  removeSimpleCharacter(charId) {
    this.midground.removeChild(this.simpleChars[charId]);
    this.simpleChars[charId].destroy();
    delete this.simpleChars[charId];
  }

  moveSimpleCharacter(charId, x, time) {
    this.simpleChars[charId].runTo(x, time);
  }
}
